import { Document, Packer, Paragraph, HeadingLevel, Table, TableRow, TableCell, TextRun, WidthType } from 'docx';
import type { ScoredGeography } from '@/types/insights';
import type { ReportFilters } from '@/types/report';
import { describeFilters } from '@/reports/filterSummary';
import { round } from '@/utils/numbers';
import { downloadBlob } from './download';

const HEADERS = ['County', 'Median Sale Price', 'Days on Market', 'New Listings', 'Closed Sales', 'Sale/List %', 'Hot/Cold'];

function headerRow(): TableRow {
  return new TableRow({
    children: HEADERS.map(
      (h) =>
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })],
        }),
    ),
  });
}

function dataRow({ record, insights }: ScoredGeography): TableRow {
  const cells = [
    record.geoName,
    record.resolved.medianSalePrice != null ? `$${record.resolved.medianSalePrice.toLocaleString()}` : '--',
    String(record.resolved.medianDaysOnMarket ?? '--'),
    String(record.resolved.newListings ?? '--'),
    String(record.resolved.closedSales ?? '--'),
    record.resolved.saleToListRatioPct != null ? `${round(record.resolved.saleToListRatioPct, 1)}%` : '--',
    `${insights.hotColdScore.label} (${round(insights.hotColdScore.value, 0)})`,
  ];
  return new TableRow({
    children: cells.map((text) => new TableCell({ children: [new Paragraph(text)] })),
  });
}

/**
 * Generates the Report Builder's Word (.docx) export via the `docx` package
 * (browser-compatible object model -> Blob), matching the title/filter
 * summary/timestamp/table/observations shape used by the PDF and Excel
 * exports so all three formats present the same report.
 */
export async function exportReportWord(rows: ScoredGeography[], filters: ReportFilters, filename = 'texas-market-trends-report.docx') {
  const doc = new Document({
    sections: [
      {
        children: [
          new Paragraph({ text: 'Texas Market Trends Report', heading: HeadingLevel.HEADING_1 }),
          new Paragraph({ text: describeFilters(filters) }),
          new Paragraph({
            text: `Generated ${new Date().toLocaleString()}  |  ${rows.length} ${rows.length === 1 ? 'county' : 'counties'} matched`,
          }),
          new Paragraph({ text: '' }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [headerRow(), ...rows.map(dataRow)],
          }),
          new Paragraph({ text: '' }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Figures are derived by MAY Assistant from public source data (see in-app source list) and are not official published figures. Price-per-square-foot is not yet available in this dataset.',
                italics: true,
                size: 18,
              }),
            ],
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, filename);
}
